/**
 * Connectivity detection that does NOT trust `navigator.onLine`.
 *
 * `navigator.onLine` is inherently unreliable and can be stuck `false` while the
 * network actually works — a long-standing, cross-browser bug (MDN: "inherently
 * unreliable … you should not disable features based on the online status").
 * Firebase's Installations SDK trusts the flag and throws `installations/app-offline`
 * before any network call, which blocks `getToken()` entirely.
 *
 * Strategy (proven against a real stuck-offline browser):
 *  1. If `navigator.onLine` is true, believe it.
 *  2. If it's false, run a REAL network probe. The probe — not the flag — is the
 *     source of truth (this is the MDN-recommended approach).
 *  3. If the probe confirms connectivity, force `navigator.onLine` to report `true`
 *     so Firebase's SDK stops bailing. This override is the one deliberate hack;
 *     blast radius is tiny (nothing else in the seller app reads `navigator.onLine`),
 *     and it's only applied AFTER a probe confirms we're genuinely online.
 */

/** A 204-no-content endpoint designed for connectivity checks. */
const PROBE_URL = "https://www.gstatic.com/generate_204";
const PROBE_TIMEOUT_MS = 4000;

/** Set once we've overridden `navigator.onLine`, so we only do it a single time. */
let onlineFlagForced = false;

/**
 * Real connectivity probe. A `no-cors` GET resolves (opaque) once the request
 * completes at the network level and rejects only on a genuine network failure
 * (DNS / no route / abort). Bounded by a timeout so a hang reads as "offline".
 */
export async function probeConnectivity(
  timeoutMs: number = PROBE_TIMEOUT_MS,
): Promise<boolean> {
  if (typeof fetch === "undefined") return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    await fetch(PROBE_URL, {
      mode: "no-cors",
      cache: "no-store",
      signal: controller.signal,
    });
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Idempotently override `navigator.onLine` to always report `true`. Only call
 * this once a probe has confirmed real connectivity — it exists purely to stop
 * Firebase's Installations SDK from short-circuiting on a false-negative flag.
 */
export function forceOnlineFlag(): void {
  if (onlineFlagForced || typeof navigator === "undefined") return;
  try {
    Object.defineProperty(navigator, "onLine", {
      configurable: true,
      get: () => true,
    });
    onlineFlagForced = true;
    console.info(
      "[connectivity] navigator.onLine was a false-negative; forced to true after a successful probe",
    );
  } catch {
    // Some engines may forbid redefining the property — nothing else to do.
  }
}

/**
 * True if we are effectively online: the flag says so, OR a real probe confirms
 * it. When the flag is a false-negative but the probe passes, also force the flag
 * so downstream Firebase calls proceed instead of throwing `app-offline`.
 */
export async function ensureEffectivelyOnline(): Promise<boolean> {
  if (typeof navigator === "undefined") return true;
  if (navigator.onLine) return true;
  const online = await probeConnectivity();
  if (online) forceOnlineFlag();
  return online;
}
